import { useCallback, useContext, useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { FontSizes } from '@calm-web/design-system';

import FAQ from '@/components/homepageRefresh/homepage/FAQ';
import HomepageFAQ from '@/components/homepageRefresh/homepage/HomepageLayout/data/faq.mdx';
import {
	messages as toolsMessages,
	sections as toolsSections,
} from '@/components/homepageRefresh/homepage/HomepageLayout/data/tools';
import { GradientBg } from '@/components/homepageRefresh/homepage/HomepageLayout/styles';
import MentalHealthTools from '@/components/homepageRefresh/homepage/MentalHealthTools';
import ValueProps from '@/components/homepageRefresh/homepage/ValueProps';
import Hero from '@/components/subpages/Hero';
import { useAnalytics } from '@/hooks/analytics';

import { PreSignupFlowContext } from '../../PreSignupProvider';
import messages from './messages';
import { LandingPageWrapper, SectionWrapper } from './styles';

const FocusedLandingPage = () => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { nextScreen, skipToEnd, ActiveScreen } = useContext(PreSignupFlowContext);
	const {
		heroHeading,
		heroBody,
		heroBgImage,
		heroHeaderColor,
		heroSecondaryCTAButtonProperties,
		heroCTAButtonProperties,
		toolCTAButtonProperties,
		marqueValueProps = [],
		faqs,
		toolSections: configuredToolSections,
		toolsHeading,
		toolsSubheading,
	} = ActiveScreen;
	const [marquesValueProp, setMarquesValueProp] = useState<MessageDescriptor | undefined>(
		marqueValueProps[0],
	);
	const [propIdx, setPropIdx] = useState(0);

	function userClickedSkipToEnd() {
		logEvent({
			eventName: 'Landing Page : Signup Flow CTA : Clicked',
			eventProps: {
				event_location: 'Hero',
				event_action: 'Skip to End',
			},
		});
		skipToEnd();
	}

	function userClickedNextScreen() {
		logEvent({
			eventName: 'Landing Page : Signup Flow CTA : Clicked',
			eventProps: {
				event_location: 'Hero',
				event_action: 'Next Screen',
			},
		});
		nextScreen();
	}

	const _heroSecondaryCTAButtonProperties = heroSecondaryCTAButtonProperties
		? {
				label: formatMessage(heroSecondaryCTAButtonProperties.label),
				onClick: userClickedNextScreen,
			}
		: undefined;

	const updateMarquesValueProp = useCallback(() => {
		setPropIdx(prev => (prev + 1) % marqueValueProps.length);
		setMarquesValueProp(marqueValueProps[propIdx]);
	}, [marqueValueProps, propIdx]);

	useEffect(() => {
		const id = setInterval(() => {
			updateMarquesValueProp();
		}, 2000);
		return () => clearInterval(id);
	}, [propIdx, updateMarquesValueProp]);

	const CTAButtonConfig = {
		label: formatMessage(heroCTAButtonProperties?.label ?? messages.cta),
		onClick: userClickedSkipToEnd,
	};

	function userSkippedToEndTools() {
		logEvent({
			eventName: 'Landing Page : Signup Flow CTA : Clicked',
			eventProps: {
				event_location: 'Tools',
				event_action: 'Skip to End',
			},
		});
		skipToEnd();
	}

	const toolsCTAConfig = {
		label: toolCTAButtonProperties?.label ?? messages.cta,
		onClick: userSkippedToEndTools,
	};

	return (
		<>
			<LandingPageWrapper>
				<Hero
					marqueValueProp={marquesValueProp}
					heroHeading={heroHeading ?? messages.heroHeading}
					heroBody={heroBody ?? messages.heroBody}
					heroCta={heroHeading ?? messages.heroHeading}
					heroBgImage={heroBgImage ?? '/_n/images/subpages/stress-hero.webp'}
					showDownArrow={false}
					CTAButtonProperties={CTAButtonConfig}
					secondaryCTAButtonProperties={_heroSecondaryCTAButtonProperties}
					isCenteredCopy={true}
					heroHeaderColor={heroHeaderColor ?? 'blue7'}
					heroBodySize={FontSizes.lg}
					primaryCTAColor="accessibleBrandGradient"
				/>
			</LandingPageWrapper>
			<div>
				<SectionWrapper>
					<ValueProps noLink={true} onClickEvent={CTAButtonConfig.onClick} />
				</SectionWrapper>
				<GradientBg>
					<MentalHealthTools
						hideFreeSampleLabel={true}
						toolsSections={configuredToolSections ?? toolsSections}
						toolsHeading={toolsHeading ?? toolsMessages.heading}
						toolsCopy={toolsSubheading ?? toolsMessages.copy}
						toolsCTAConfig={toolsCTAConfig}
						toolImageBorderBakedIn={true}
					/>
				</GradientBg>
				<GradientBg>
					<SectionWrapper>
						<FAQ FaqSections={faqs || HomepageFAQ} />
					</SectionWrapper>
				</GradientBg>
			</div>
		</>
	);
};

export default FocusedLandingPage;
